import { useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowDown, ArrowUpRight, Compass, MoveUpRight, Sparkles, Target, Zap } from 'lucide-react';
import { useAuth } from '../../../services/authentication/authContext';
import { Header } from '../landingpage/sections/Header';
import Footer from '../landingpage/sections/Footer';
import CareersRoles from './CareersRoles';

const values = [
  { key: 'move', Icon: Zap },
  { key: 'own', Icon: Target },
  { key: 'learn', Icon: Sparkles },
  { key: 'aim', Icon: MoveUpRight },
] as const;
const benefits = ['responsibility', 'founders', 'growth', 'shape'] as const;
export default function CareersPage() {
  const { t } = useTranslation();
  const { user, loading } = useAuth();
  const pageTitle = t('careers.pageTitle');

  useEffect(() => {
    if (user || loading) return;
    const previousTitle = document.title;
    const existingRobots = document.querySelector<HTMLMetaElement>('meta[name="robots"]');
    const robots = existingRobots ?? document.createElement('meta');
    const previousContent = robots.getAttribute('content');
    robots.name = 'robots';
    robots.content = 'noindex, nofollow';
    if (!existingRobots) document.head.appendChild(robots);
    document.title = pageTitle;

    return () => {
      document.title = previousTitle;
      if (!existingRobots) robots.remove();
      else if (previousContent === null) robots.removeAttribute('content');
      else robots.setAttribute('content', previousContent);
    };
  }, [user, loading, pageTitle]);

  // The preview belongs to the signed-out marketing experience. Existing app
  // guards retain priority for setup, payment and restricted accounts.
  if (user) return <Navigate to="/" replace />;
  if (loading) return <div role="status" aria-label={t('common.loading')} aria-busy="true" className="min-h-screen bg-landingPageBG dark:bg-landingPageBG-dark" />;

  return (
    <div className="-mb-28 min-h-screen break-words bg-white text-primary dark:bg-backgroundDarker-dark dark:text-primary-dark lg:mb-0">
      <Header homeAnchors compactLogo />

      <section aria-labelledby="careers-title" className="relative overflow-hidden bg-landingPageBG px-5 pb-16 pt-12 text-white dark:bg-landingPageBG-dark dark:text-white sm:px-8 sm:pb-20 lg:pt-20">
        <div aria-hidden="true" className="pointer-events-none absolute -right-32 -top-32 h-96 w-96 rounded-full border border-white/10 dark:border-white/10" />
        <div aria-hidden="true" className="pointer-events-none absolute -right-16 -top-16 h-64 w-64 rounded-full border border-white/10 dark:border-white/10" />
        <div className="relative mx-auto max-w-[1140px]">
          <p className="mb-8 flex items-center gap-3 text-sm font-semibold tracking-wide text-emerald-300 dark:text-emerald-300">
            <span aria-hidden="true" className="h-2 w-2 rounded-full bg-emerald-300 dark:bg-emerald-300" />
            {t('careers.eyebrow')}
          </p>
          <h1 id="careers-title" className="max-w-full text-[42px] font-semibold leading-[1.08] tracking-tight sm:text-6xl lg:text-[64px] xl:text-7xl">
            <span className="block lg:whitespace-nowrap">{t('careers.hero.title')}</span>
            <span className="text-emerald-300 dark:text-emerald-300">{t('careers.hero.titleAccent')}</span>
          </h1>
          <div className="mt-10 grid gap-8 border-t border-white/15 pt-8 dark:border-white/15 lg:mt-14 lg:grid-cols-[1.1fr_1fr] lg:gap-20 lg:pt-10">
            <p className="max-w-xl text-xl leading-relaxed text-white dark:text-white sm:text-2xl">{t('careers.hero.ambition')}</p>
            <div className="space-y-5 text-base leading-7 text-white/75 dark:text-white/75 sm:text-lg">
              <p>{t('careers.hero.product')}</p>
              <p>{t('careers.hero.team')}</p>
            </div>
          </div>
          <a href="#careers-roles" className="mt-9 inline-flex min-h-[48px] items-center gap-3 rounded-xl bg-emerald-300 px-5 py-3 text-base font-semibold text-slate-900 transition-colors hover:bg-emerald-200 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-white dark:bg-emerald-300 dark:text-slate-900 dark:hover:bg-emerald-200 dark:focus-visible:outline-white">
            {t('careers.hero.explore')}<ArrowDown size={18} aria-hidden="true" />
          </a>
        </div>
      </section>

      <div className="mx-auto max-w-[1240px] px-5 py-16 sm:px-8 sm:py-24 lg:px-12">
        <section aria-labelledby="careers-mission" className="grid gap-8 lg:grid-cols-[1fr_1.2fr] lg:gap-20">
          <div>
            <p className="mb-4 text-xs font-semibold uppercase tracking-[0.16em] text-emerald-700 dark:text-emerald-300">{t('careers.mission.eyebrow')}</p>
            <h2 id="careers-mission" className="max-w-sm text-3xl font-semibold leading-tight tracking-tight sm:text-4xl">{t('careers.mission.title')}</h2>
          </div>
          <div className="space-y-5 text-base leading-8 text-secondary dark:text-secondary-dark sm:text-lg">
            <p>{t('careers.mission.problem')}</p>
            <p className="font-semibold text-primary dark:text-primary-dark">{t('careers.mission.change')}</p>
            <p>{t('careers.mission.purpose')}</p>
          </div>
        </section>

        <section aria-labelledby="careers-team" className="mt-16 rounded-3xl bg-slate-50 p-6 dark:bg-slate-800/60 sm:mt-24 sm:p-10 lg:p-12">
          <div className="grid gap-8 lg:grid-cols-[1fr_1.2fr] lg:gap-20">
            <div>
              <Compass size={32} strokeWidth={1.5} aria-hidden="true" className="mb-6 text-emerald-700 dark:text-emerald-300" />
              <h2 id="careers-team" className="max-w-sm text-3xl font-semibold leading-tight tracking-tight sm:text-4xl">{t('careers.team.title')}</h2>
            </div>
            <div className="space-y-5 text-base leading-8 text-secondary dark:text-secondary-dark sm:text-lg">
              <p>{t('careers.team.close')}</p>
              <p>{t('careers.team.initiative')}</p>
              <p>{t('careers.team.build')}</p>
              <p>{t('careers.team.opportunity')}</p>
            </div>
          </div>
        </section>

        <section aria-labelledby="careers-values" className="pt-16 sm:pt-24">
          <p className="mb-4 text-xs font-semibold uppercase tracking-[0.16em] text-emerald-700 dark:text-emerald-300">{t('careers.values.eyebrow')}</p>
          <h2 id="careers-values" className="text-3xl font-semibold tracking-tight sm:text-4xl">{t('careers.values.title')}</h2>
          <div className="mt-9 grid gap-5 sm:grid-cols-2 sm:gap-6">
            {values.map(({ key, Icon }, index) => (
              <article key={key} className="rounded-2xl border border-slate-200 p-6 dark:border-slate-700 sm:p-8">
                <div className="mb-7 flex items-center justify-between">
                  <Icon size={26} strokeWidth={1.5} aria-hidden="true" className="text-emerald-700 dark:text-emerald-300" />
                  <span aria-hidden="true" className="text-sm tabular-nums text-slate-400 dark:text-slate-500">0{index + 1}</span>
                </div>
                <h3 className="mb-3 text-2xl font-semibold tracking-tight">{t(`careers.values.${key}.title`)}</h3>
                <p className="text-base leading-7 text-secondary dark:text-secondary-dark">{t(`careers.values.${key}.body`)}</p>
              </article>
            ))}
          </div>
        </section>

        <section aria-labelledby="careers-benefits" className="pt-16 sm:pt-24">
          <h2 id="careers-benefits" className="text-3xl font-semibold tracking-tight sm:text-4xl">{t('careers.benefits.title')}</h2>
          <div className="mt-10 grid gap-x-12 gap-y-9 sm:grid-cols-2">
            {benefits.map(key => (
              <div key={key} className="border-l-2 border-emerald-600 pl-5 dark:border-emerald-400">
                <h3 className="mb-2 text-lg font-semibold">{t(`careers.benefits.${key}.title`)}</h3>
                <p className="max-w-md text-base leading-7 text-secondary dark:text-secondary-dark">{t(`careers.benefits.${key}.body`)}</p>
              </div>
            ))}
          </div>
        </section>
      </div>

      <section aria-labelledby="careers-people" className="bg-landingPageBG px-5 py-16 text-white dark:bg-landingPageBG-dark dark:text-white sm:px-8 sm:py-20">
        <div className="mx-auto max-w-[760px]">
          <p className="mb-4 text-xs font-semibold uppercase tracking-[0.16em] text-emerald-300 dark:text-emerald-300">{t('careers.people.eyebrow')}</p>
          <h2 id="careers-people" className="mb-8 text-3xl font-semibold tracking-tight sm:text-4xl">{t('careers.people.title')}</h2>
          <div className="space-y-5 text-lg leading-8 text-white/80 dark:text-white/80">
            <p className="font-medium text-white dark:text-white">{t('careers.people.intro')}</p>
            <p>{t('careers.people.character')}</p>
            <p>{t('careers.people.growth')}</p>
            <p>{t('careers.people.systems')}</p>
          </div>
        </div>
      </section>

      <section id="careers-roles" aria-labelledby="careers-roles-title" className="scroll-mt-28 px-5 py-16 sm:px-8 sm:py-24">
        <div className="mx-auto max-w-[1140px]">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between sm:gap-8">
            <div>
              <p className="mb-4 text-xs font-semibold uppercase tracking-[0.16em] text-emerald-700 dark:text-emerald-300">{t('careers.roles.eyebrow')}</p>
              <h2 id="careers-roles-title" className="text-3xl font-semibold tracking-tight sm:text-4xl">{t('careers.roles.title')}</h2>
            </div>
            <p className="max-w-md text-base leading-7 text-secondary dark:text-secondary-dark">{t('careers.roles.intro')}</p>
          </div>
          <CareersRoles />
          <a href="/" className="mt-9 inline-flex min-h-[44px] items-center gap-2 rounded text-sm font-semibold text-emerald-700 underline-offset-4 hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-emerald-700 dark:text-emerald-300 dark:focus-visible:outline-emerald-300">
            {t('careers.backHome')}<ArrowUpRight size={16} aria-hidden="true" />
          </a>
        </div>
      </section>

      <Footer homeAnchors />
    </div>
  );
}
